import { useEffect, useState } from 'react';
import { yupValidator } from '@lib/yup-validator';
import { useForm } from 'react-hook-form';

import { fdCalculatorSchema } from '@components/Products/FixedDeposits/utils/schema';

const calculateFDMaturity = ({
    principle,
    interest,
    tenure,
    compoundingFrequency,
}) => {
    return (
        principle *
        Math.pow(
            1 + interest / (compoundingFrequency * 100),
            compoundingFrequency * tenure
        )
    );
};

const calculateFD = (principle, interest, tenure) => {
    if (!principle || !interest || !tenure)
        return { maturityAmount: 0, returns: 0 };

    const compoundingFrequency = 4; // Assuming interest is compounded quarterly
    const maturityAmount = calculateFDMaturity({
        principle,
        interest,
        tenure,
        compoundingFrequency,
    });
    const gain = maturityAmount - principle;

    return {
        maturityAmount: Math.round(maturityAmount),
        returns: Math.round(gain),
    };
};

const useFDCalculator = () => {
    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors },
        setValue,
    } = useForm({
        resolver: yupValidator(fdCalculatorSchema),
        defaultValues: {
            investmentAmount: 1000000,
            interestRate: 9.5,
            tenure: 4,
        },
    });

    const investmentAmount = watch('investmentAmount') || 0;
    const interestRate = watch('interestRate') || 0;
    const tenure = watch('tenure') || 0;

    const [totalInvested, setTotalInvested] = useState(investmentAmount);
    const [estimatedReturns, setEstimatedReturns] = useState(0);
    const [totalValue, setTotalValue] = useState(0);

    useEffect(() => {
        const { maturityAmount, returns } = calculateFD(
            investmentAmount,
            interestRate,
            tenure
        );

        setTotalInvested(investmentAmount);
        setEstimatedReturns(returns);
        setTotalValue(maturityAmount);
        setValue('totalInvested', investmentAmount);
        setValue('estimatedReturns', returns);
        setValue('totalValue', maturityAmount);
    }, [investmentAmount, interestRate, tenure, setValue]);

    const handleInputChange = (input, val, field) => {
        let value = val;

        if (input.id === 'interestRate') {
            value = value.replace(/[^0-9.]/g, '');
            if ((value.match(/\./g) || []).length > 1) return;
            if (value.includes('.')) {
                const [intPart, decPart = ''] = value.split('.');
                value = `${intPart}.${decPart.substring(0, 2)}`;
            }
        } else {
            value = value.replace(/[^0-9]/g, '');
        }

        if (parseFloat(value) > input.max) {
            field.onChange(input.max);
        } else {
            field.onChange(value);
        }
    };

    const handleBlur = (input, val, field) => {
        let value = val;
        if (input.id === 'interestRate' && value.endsWith('.')) {
            value += '0';
        }

        if (input.id === 'investmentAmount') {
            value = value.replace(/,/g, '');
        }
        if (parseFloat(value) % 1 === 0) {
            value = parseInt(value, 10).toString();
        }
        if (value === '' || parseFloat(value) < input.min) {
            field.onChange(input.min);
        } else {
            field.onChange(value);
        }
    };

    return {
        states: {
            investmentAmount,
            interestRate,
            tenure,
            totalInvested,
            estimatedReturns,
            totalValue,
            errors,
        },
        callbacks: {
            register,
            handleSubmit,
            control,
            setValue,
            handleInputChange,
            handleBlur,
        },
    };
};

export default useFDCalculator;
